import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ExternalLink, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { SectionHead, Sel } from "./fields";
import BookingRules from "./BookingRules";

const STATUSES = ["New", "Confirmed", "Completed", "Cancelled"];
const tone = { New: "bg-brand-accent/15 text-brand-accent-dark", Confirmed: "bg-emerald-100 text-emerald-800", Completed: "bg-brand-sand text-brand-muted", Cancelled: "bg-red-50 text-red-700" };

export default function BookingsAdmin({ onChange }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/admin/bookings").then((r) => setRows(r.data)).catch((e) => toast.error(formatErr(e))); }, []);
  if (!rows) return <Loader2 className="w-6 h-6 animate-spin text-brand-accent" />;

  const update = async (b, status) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${b.id}`, { status });
      setRows(rows.map((r) => (r.id === b.id ? data : r)));
      toast.success(`Booking marked ${status}`);
      onChange?.();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const list = filter === "all" ? rows : rows.filter((b) => b.status === filter);

  return (
    <div data-testid="admin-bookings">
      <SectionHead title="Site visit bookings" sub={`${rows.filter((b) => b.status === "New").length} new · ${rows.length} total`}
        action={<div className="inline-flex rounded-full bg-brand-sand p-1 border border-brand-line">
          {["all", ...STATUSES].map((s) => <button key={s} onClick={() => setFilter(s)} data-testid={`bookings-filter-${s.toLowerCase()}`} className={`px-3 py-1.5 rounded-full text-xs transition-colors ${filter === s ? "bg-brand-ink text-brand-cream" : "text-brand-muted"}`}>{s === "all" ? "All" : s}</button>)}
        </div>} />
      <BookingRules />
      <div className="space-y-3">
        {list.map((b) => (
          <div key={b.id} className="rounded-2xl border border-brand-line bg-brand-cream p-5 grid md:grid-cols-12 gap-4 items-center" data-testid={`admin-booking-${b.id}`}>
            <div className="md:col-span-3">
              <p className="text-brand-ink">{b.name}</p>
              <p className="text-xs text-brand-muted inline-flex items-center gap-1"><Phone className="w-3 h-3" />{b.phone} · {b.email}</p>
            </div>
            <div className="md:col-span-3">
              <p className="text-sm text-brand-ink">{b.visit_type}</p>
              <p className="text-xs text-brand-muted">{new Date(b.date + "T00:00:00").toLocaleDateString("en-IN", { dateStyle: "medium" })} · {b.time_window}</p>
            </div>
            <div className="md:col-span-3 text-xs text-brand-muted">
              <p className="line-clamp-2">{b.address}</p>
              {b.note && <p className="italic mt-1 line-clamp-1">“{b.note}”</p>}
              <Link to={`/quote/${b.quote_id}`} target="_blank" className="inline-flex items-center gap-1 text-brand-accent hover:text-brand-accent-dark mt-1 font-mono-num" data-testid={`admin-booking-quote-${b.id}`}>{b.quote_number} <ExternalLink className="w-3 h-3" /></Link>
            </div>
            <div className="md:col-span-3 flex items-center justify-end gap-3">
              <span className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full ${tone[b.status]}`} data-testid={`admin-booking-status-${b.id}`}>{b.status}</span>
              <div className="w-36"><Sel value={b.status} onChange={(v) => update(b, v)} options={STATUSES.map((s) => [s, s])} testid={`admin-booking-select-${b.id}`} /></div>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="rounded-2xl border border-dashed border-brand-line p-12 text-center text-sm text-brand-muted" data-testid="admin-bookings-empty">No bookings here yet.</div>}
      </div>
    </div>
  );
}
